/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Card, CardContent, makeStyles, Typography } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { MapBoundsContext, trimTrailingSlash } from '@terragotech/gen5-shared-components';
import { MAP_SERVICE_TYPE } from '@terragotech/gen5-shared-utilities';
import { Polygon, Properties } from '@turf/helpers';
import { Feature } from 'geojson';
import { History } from 'history';
import * as MapboxGl from 'mapbox-gl';
import { GeolocateControl } from 'mapbox-gl';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MapEvent } from 'react-mapbox-gl/lib/map-events';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import useRouteParams from '../components/Common/useRouteParams';
import AssetCardRenderer, { AssetCardRendererRef } from '../components/Map/AssetCard/AssetCardRenderer';
import { MapInstance, MaxZoomClickHandler, SymbolClickProps } from '../components/Map/component/Map';
import useDrawFeature from '../components/Map/component/useDrawFeature';
import TGPageMapField from '../components/Page/TGMapField';
import { useAggregates } from '../contexts/AggregatesContext';
import { MapAssetType } from '../contexts/AggregatesContext/types';
import { useAssetCards } from '../contexts/assetCardContext';
import { AssetsDashboardContext } from '../contexts/assetsDashboardContext';
import { useTableColumns } from '../contexts/TableColumnContext';
import {
  configState,
  drawFilterSelector,
  drawToolDrawnState,
  getRecordFilterCallback,
  mapBoundsState,
  mapLabelSelector,
  selectedItemsState,
  selectedRecordTypeState,
} from '../recoil/atoms';
import { colors } from '../styles/theme';
import { arcGisParams, rasterSourceData, wmsParams } from '../utils/mapInfo';
import { getQuery } from '../contexts/AggregatesContext/useAssetsLoader';
import { LanguageContext } from '../contexts/LanguageContext/languageContext';
import { getProjectIdsCallback, selectedProjectsState } from '../recoil/atoms/project';

export interface MapOverviewContainerProps {
  height?: unknown;
  width?: number;
  classes: any;
}

/**
 * @remarks Obtains variables for props, returns Map with props
 *
 * @param props
 */
const MapOverviewContainer: React.FunctionComponent<MapOverviewContainerProps> = props => {
  const { height, width } = props;
  const selectedRecordType = useRecoilValue(selectedRecordTypeState);
  const mapLabelVisibility = useRecoilValue(mapLabelSelector);

  const match: { params: { assetId?: string } } = useRouteMatch({
    path: `/${selectedRecordType}/:assetId?/:comparison(map|data|edit)?`,
  }) || { params: {} };
  const [mapAssets, setMapAssets] = useState<MapAssetType[]>([]);
  const { setAssetsIds, setCoordinates, assetIds } = useAssetCards();
  const assetIdsLength = useRef(assetIds.length);
  const history: History = useHistory();
  const [assetId, setAssetId] = useState('');
  const setGlobalItems = useSetRecoilState(selectedItemsState);
  const [selectedClusterId, setSelectedClusterId] = useState<number | null>(null);
  const [mapBoxInstance, setMapBoxInstance] = useState<MapboxGl.Map | null>(null);  // TODO: [S5-3775] Ensure this doesn't cause multiple subscriptions in `useDrawFeature`.
  const [mapInstance, setMapInstance] = useState<MapInstance | null>(null);
  const [mapViewBox, setMapViewBox] = useState<Feature<Polygon, Properties>>();
  const [mapBounds, setMapBounds] = useRecoilState(mapBoundsState);
  const [mapRasterSources, setMapRasterSources] = useState<Record<string, MapboxGl.RasterSource>>();
  const { applyDrawFilter, undoRecentChanges, clearAll, isDrawActive } = useDrawFeature(mapBoxInstance);
  const { setMapCenter, currentZoomLevel, mapCenter } = useContext(MapBoundsContext);
  const assetCardRef = useRef<AssetCardRendererRef>(null);
  const { setCurrentAssetId } = useTableColumns();
  // Adding custom mapbox map control for current location button, because it is not available in our mapbox-react library
  // https://docs.mapbox.com/mapbox-gl-js/api/markers/#geolocatecontrol.event:trackuserlocationstart
  const onMapLoad: MapEvent = useCallback(map => {
    setMapBoxInstance(map);
    map.addControl(
      new GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: true,
      }),
      'top-right'
    );
  }, []);

  useEffect(() => {
    assetIdsLength.current = assetIds.length;
  }, [assetIds]);

  const { aggregateDefinitions } = useRecoilValue(configState);
  const selectedProjects = useRecoilValue(selectedProjectsState);
  const getAllProjectIds = useRecoilCallback(getProjectIdsCallback, []);

  const getFilterState = useRecoilCallback(getRecordFilterCallback, []);
  const [zoom] = useState(currentZoomLevel);

  const [center] = useState<[number, number]>(mapCenter);
  const assetRecord = useAggregates();
  const { isAssetDetailsOpen } = useRouteParams({
    selectedRecordType,
  });
  const getAssetId = () => {
    const pattern = new RegExp(`/${selectedRecordType}/?(.+)?/?(map|data|edit)?`);
    const pathMatch = history.location.pathname.match(pattern);
    if (pathMatch) {
      const match = pathMatch?.[1]?.split('/');
      setAssetId(match?.[0] || '');
    }
  };
  const { setIsCardVisible } = useAssetCards();
  useEffect(() => {
    if (!isAssetDetailsOpen) {
      getAssetId();
      setIsCardVisible(true);
    }
  }, [selectedRecordType, match.params?.assetId, isAssetDetailsOpen, setIsCardVisible]);

  const getRecordType = useCallback(
    (mapData: MapAssetType[], feature: Feature | undefined, assetId: string) => {
      if (assetId) {
        const type = mapData.find(x => x.id === assetId)?.recordTypeKey;
        if (type) {
          const selectedDefinitionName = aggregateDefinitions.find(x => x.queryKey === type)?.name;
          return selectedDefinitionName ?? '';
        } else {
          const aggregateTypeDefinitionName = aggregateDefinitions.find(
            x => x.queryKey === feature?.properties?.aggregateType
          )?.name;
          return aggregateTypeDefinitionName ?? '';
        }
      }
      return '';
    },
    [assetId, aggregateDefinitions]
  );

  //Various event handlers
  const handleSymbolClick = useCallback(({ feature }: SymbolClickProps) => {
    setSelectedClusterId(0);
    if (feature) {
      let recordType = selectedRecordType;
      const assetId = feature.properties?.assetId ?? '';
      const assetDataRecordType = getRecordType([], feature, assetId);
      const newRecordType =
        assetDataRecordType?.length > 0 ? assetDataRecordType : getRecordType(mapAssets, feature, assetId);
      recordType = newRecordType.length > 0 ? newRecordType : recordType;
      if (history.location.pathname === `/${recordType}/${assetId}`) {
        history.push(`/${recordType}`);
        setAssetsIds([]);
      } else {
        setAssetsIds([assetId]);
        history.push(`/${recordType}/${assetId}`);
      }
    }
  }, [history, selectedRecordType, mapAssets, getRecordType]);

  const handleMaxZoomClusterClick = useCallback<MaxZoomClickHandler>((map, feature, coordinate) => {
    if (map && feature && feature.id && feature.geometry.type === 'Point') {
      const clusterId = typeof feature.id === 'string' ? parseFloat(feature.id) : feature.id;
      const source = map.getSource('symbolSource') as MapboxGl.GeoJSONSource;
      source.getClusterLeaves(clusterId, Infinity, 0, (error, features) => {
        if (error) {
          console.error(error);
          return;
        }
        const assetIds = features.map(feature => (feature.properties ? feature.properties.assetId : ''));
        setAssetsIds(assetIds);
        setCoordinates(coordinate);
      });
    }
  }, []);

  useEffect(() => {
    // For now, handling all layers as individual sources so each layer can be toggled.
    const rasterSources: Record<string, MapboxGl.RasterSource> = {};
    assetRecord.mapServiceLayers.forEach(sl => {
      let serviceParams: Partial<MapboxGl.RasterSource> = {};
      switch (sl.serviceType) {
        case MAP_SERVICE_TYPE.WMS:
          serviceParams = {
            // Use the tiles option to specify a WMS tile source URL. https://docs.mapbox.com/mapbox-gl-js/style-spec/sources/
            tiles: [
              `${sl.serviceUri}?bbox=${wmsParams.bbox}&${new URLSearchParams({
                ...(Object.fromEntries(
                  Object.entries(wmsParams).filter(([k, v]) => k !== 'bbox' && v != null)
                ) as Record<string, string>),
                layers: sl.layer.layerName, // sl.layers.map(x => x.id).join(','), // Reimplement when multiple layer names (within each toggle) are supported by the aggregate.
                //styles: sl.layers.map(x => x.style || '').join(','), // Reimplement when styles are added supported by the aggregate.
              }).toString()}`,
            ],
          };
          break;
        case MAP_SERVICE_TYPE.ArcGIS:
          serviceParams = {
            tiles: [
              `${trimTrailingSlash(sl.serviceUri)}/export?bbox=${arcGisParams.bbox}&${new URLSearchParams({
                ...(Object.fromEntries(
                  Object.entries(arcGisParams).filter(([k, v]) => k !== 'bbox' && v != null)
                ) as Record<string, string>),
                layers: `show:${sl.layer.layerName}`, // `show:${s.layers.map(x => x.id).join(',')}`, // Reimplement when multiple layer names (within each toggle) are supported by the aggregate.
                //styles: s.layers.map(x => x.style || '').join(','), // Reimplement when styles are added supported by the aggregate.
              }).toString()}`,
            ],
          };
          break;
        default:
          console.error(`Map Service Layer ${sl.id} has an invalid service type: ${sl.serviceType}`);
          break;
      }

      if (Object.keys(serviceParams).length) {
        rasterSources[sl.id] = { ...rasterSourceData, ...serviceParams };
      }
    });
    setMapRasterSources(rasterSources);
  }, [assetRecord.mapServiceLayers, setMapRasterSources]);

  const onCardClose = () => {
    setCurrentAssetId(o => ({ ...o, id: '' }));
    if (history.location.pathname.match(/\/\w+\//)) {
      history.push(history.location.pathname.match(/(\/\w+)\//)?.[1] || '');
    }
    setSelectedClusterId(0);
  };

  return (
    <>
      <TGPageMapField
        hideVisibility={false}
        setMapAssets={setMapAssets}
        height="100%"
        mapBounds={mapBounds}
        setMapBounds={setMapBounds}
        setItems={setGlobalItems}
        aggregates={aggregateDefinitions
          .filter(d => !d.hiddenFromMapView)
          .map(d => getQuery(d, getFilterState(d.name), selectedProjects, assetRecord, getAllProjectIds()))
          .filter(a => a)}
        mapParams={{
          onMaxZoomClusterClick: handleMaxZoomClusterClick,
          height: height,
          width: width,
          desiredZoom: zoom,
          desiredCenter: center,
          onStyleLoad: onMapLoad,
          onSymbolClick: handleSymbolClick,
          showMapControls: true,
          showRightControls: true,
          limitedRightControls: false,
          forceDisableClustering: false,
          showMapLabels: mapLabelVisibility,
          setMapViewBox: setMapViewBox,
          mapViewBox: mapViewBox,
          setMapCenter: setMapCenter,
          mapRasterSources: mapRasterSources,
          mapVisibleMapServiceKeys: assetRecord.visibleMapServiceKeys,
          selectedClusterId: selectedClusterId,
          setSelectedClusterId: setSelectedClusterId,
          setMapInstance: setMapInstance,
        }}
      />
      <AssetCardRenderer
        selectedAssetType={selectedRecordType}
        selectedAssetId={assetId}
        mapInstance={mapInstance}
        forceCards
        ref={assetCardRef}
        onCardClose={onCardClose}
        assetIds={assetIds}
        setAssetsIds={setAssetsIds}
        isMainMap={true}
        setSelectedAssetId={setAssetId}
        hasMapLocationSearch={true}
      />
      <DrawFilterActions
        onApply={applyDrawFilter}
        onCancel={undoRecentChanges}
        onClear={clearAll}
        isDrawActive={isDrawActive}
      />
    </>
  );
};

interface DrawActionProps {
  onClear: () => void;
  onCancel: () => void;
  onApply: () => void;
  isDrawActive: boolean;
}
const useStyles = makeStyles(theme => ({
  card: {
    position: 'absolute',
    zIndex: 999,
    bottom: 20,
    transform: 'translate(-50%,1px)',
    padding: '4px 18px',
    left: '50%',
    background: colors.white95,
    borderRadius: 5,
    boxShadow: `0px 2px 4px 0px ${colors.black10}`,
    display: 'inline-flex',
    gap: 18,
    alignItems: 'center',
  },
  cardContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 18,
    padding: 0,
    '&.MuiCardContent-root:last-child': {
      paddingBottom: 0,
    },
  },
  typography: {
    fontSize: 15,
    fontWeight: 400,
    fontStyle: 'normal',
    fontFamily: 'Roboto',
    lineHeight: 'normal',
    color: colors.black0,
  },
  buttonCancel: {
    padding: '6px 10px',
    fontSize: 15,
    fontWeight: 500,
    fontStyle: 'normal',
    fontFamily: 'Roboto',
    lineHeight: 'normal',
    color: theme.palette.primary.main,
    textTransform: 'capitalize',
  },
  clearBtn: {
    padding: '6px 5px',
  },
  buttonContainer: {
    display: 'flex',
  },
}));

const DrawFilterActions: React.FunctionComponent<DrawActionProps> = props => {
  const drawFilter = useRecoilValue(drawFilterSelector);
  const drawToolDrawn = useRecoilValue(drawToolDrawnState);
  const { onClear, onCancel, isDrawActive } = props;
  const classes = useStyles();
  const { isMobileView } = useContext(AssetsDashboardContext);
  const { translate } = useContext(LanguageContext);
  const prevDrawToolDrawnRef = useRef<boolean>(false);

  const disableDrawMode = useCallback(() => {
    onClear();
    onCancel();
  }, [onClear, onCancel]);

  useEffect(() => {
    if (!drawToolDrawn && prevDrawToolDrawnRef.current) {
      onCancel();
    }
    prevDrawToolDrawnRef.current = drawToolDrawn;
  }, [drawToolDrawn]);

  useEffect(() => {
    if (isMobileView) {
      disableDrawMode();
    }
  }, [isMobileView]);
  if (drawFilter.toolEnabled) {
    return (
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Typography className={classes.typography}>
            {translate(
              isDrawActive ? 'Double-click to complete the drawn shape.' : 'Draw Shapes on the map to filter your data.'
            )}
          </Typography>
          <div className={classes.buttonContainer}>
            <Button onClick={disableDrawMode} className={classes.buttonCancel}>
              {translate('Cancel')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  if (drawFilter.enabled && drawFilter.features.length) {
    return (
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Typography className={classes.typography}>{translate(`Drawing Enabled`)}</Typography>
          <div className={classes.buttonContainer}>
            <Button onClick={onClear} className={[classes.buttonCancel, classes.clearBtn].join(' ')}>
              {translate('Clear Drawing')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  return null;
};
const styles = () =>
  createStyles({
    popupWrapper: {
      '& .mapboxgl-popup-content': {
        backgroundColor: '#333333',
        padding: '5px',
      },
      '& .mapboxgl-popup-tip': {
        borderTopColor: '#333333',
      },
      '&:hover': {
        zIndex: `99999 !important`,
      },
    },
    popupItem: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      color: '#fff',
    },
    popupItemValue: {
      fontSize: '13px',
    },
    emptyValue: {
      color: '#A8A8A8',
      fontSize: '13px',
    },
  });
export default withStyles(styles)(MapOverviewContainer);
